import { useEffect } from 'react';

/**
 * Drive an Apollo query's polling from page visibility (spec 0008): poll at
 * `intervalMs` while the tab is visible, stop entirely while it's hidden —
 * a backgrounded ERP tab must not keep hammering the badge/rollup queries.
 *
 * Apollo 3.3 has no `skipPollAttempt`, so this drives `startPolling` /
 * `stopPolling` off `visibilitychange` instead. Pass `enabled=false` to keep
 * polling off regardless (e.g. no group selected yet).
 */
export const usePageVisiblePoll = (
  startPolling: (pollInterval: number) => void,
  stopPolling: () => void,
  intervalMs: number,
  enabled = true,
): void => {
  useEffect(() => {
    if (!enabled) {
      stopPolling();
      return undefined;
    }
    const sync = () => {
      if (document.visibilityState === 'hidden') stopPolling();
      else startPolling(intervalMs);
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      stopPolling();
    };
  }, [startPolling, stopPolling, intervalMs, enabled]);
};
